import { DownOutlined, RightOutlined } from '@ant-design/icons'
import { useTheme } from '@contexts/ThemeContext'
import { ActivityLog, ActivityLogEventType } from '@models/ActivityLog'
import {
  extractDifferencesFromLog,
  formatActivityLogValue,
  getFlattenedProperties,
} from '@utils/activityLogsToolbox'
import { Typography } from 'antd'
import { FC, useMemo, useState } from 'react'
import { CodeBlock, a11yDark } from 'react-code-blocks'

const { Text } = Typography

interface Props {
  log: ActivityLog
}

const isNonEmptyObject = (value: unknown): boolean =>
  value !== null && value !== undefined && Object.keys(value).length > 0

// A cheap presence check — no diff, no flatten — just to decide whether the
// toggle is worth showing at all. `buildTextToDisplay` (computed only once
// expanded) still decides what actually gets printed.
//
// An `updated` log whose `attributes` is empty/absent but whose `old` is not
// still has something to show on the raw path (`key: x → null`), so `old` is
// also accepted for that event — without running the diff here.
const hasDisplayableDetails = (log: ActivityLog): boolean => {
  if (isNonEmptyObject(log.properties?.attributes)) return true
  return (
    log.event === ActivityLogEventType.UPDATED &&
    isNonEmptyObject(log.properties?.old)
  )
}

// A side of a diff can be a whole entity rather than a scalar — a document going
// from nothing to something, say — and interpolating that straight into the string
// would print [object Object].
const formatSide = (value: any) =>
  value === null || value === undefined ? 'null' : formatActivityLogValue(value)

const planChangeReasons: Record<string, string> = {
  admin: 'changed from the back-office',
  age_limit: 'age limit reached at renewal',
}

// A plan change carries both plan names: show them, not the ids.
const buildPlanChangedText = (log: ActivityLog): string => {
  const previous = log.properties?.old?.plan ?? `#${log.properties?.old?.plan_id}`
  const next =
    log.properties?.attributes?.plan ?? `#${log.properties?.attributes?.plan_id}`
  const reason = log.properties?.attributes?.reason
  return [
    `Plan: ${previous} → ${next}`,
    reason ? `Reason: ${planChangeReasons[reason] ?? reason}` : null,
  ]
    .filter(Boolean)
    .join('\n')
}

const buildTextToDisplay = (log: ActivityLog): string => {
  if (log.event === ActivityLogEventType.PLAN_CHANGED) {
    return buildPlanChangedText(log)
  }

  const differences = extractDifferencesFromLog(log)
  const properties = getFlattenedProperties(log?.properties?.attributes ?? {})

  const displayedProperties =
    log.event === ActivityLogEventType.UPDATED
      ? differences
      : Object.entries(properties)

  return displayedProperties
    .filter((item) =>
      log.event === ActivityLogEventType.UPDATED
        ? item.key !== 'language'
        : item[0] !== 'language'
    )
    .map((item) => {
      if (log.event === ActivityLogEventType.UPDATED) {
        const { key, oldValue, newValue } = item
        return `${key}: ${formatSide(oldValue)} → ${formatSide(newValue)}`
      } else {
        const [key, [label, value]] = item
        return `${label ?? key}: ${String(value)}`
      }
    })
    .join('\n')
}

const DetailsCard: FC<Props> = ({ log }) => {
  const { mode } = useTheme()
  const [isExpanded, setIsExpanded] = useState(false)

  // Nothing is computed while the entry stays closed.
  const textToDisplay = useMemo(
    () => (isExpanded ? buildTextToDisplay(log) : ''),
    [log, isExpanded]
  )

  if (!hasDisplayableDetails(log)) {
    return null
  }

  return (
    <div>
      <Text
        type="secondary"
        onClick={() => setIsExpanded((previous) => !previous)}
        style={{ cursor: 'pointer' }}
      >
        {isExpanded ? <DownOutlined /> : <RightOutlined />}{' '}
        {isExpanded ? 'Close' : 'View details'}
      </Text>

      {isExpanded && (
        <div style={{ marginTop: 8 }}>
          <CodeBlock
            text={textToDisplay}
            language="json"
            theme={mode == 'dark' ? a11yDark : undefined}
            codeTagProps={{}}
            showLineNumbers={false}
            customStyle={{
              padding: '12px',
            }}
          />
        </div>
      )}
    </div>
  )
}

type DetailsCardType = {
  DetailsCard: typeof DetailsCard
}

export { DetailsCard, type DetailsCardType }
